import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Navigate, Outlet, redirect, useLoaderData, useLocation, useNavigate, useOutletContext, type LoaderFunctionArgs } from 'react-router-dom'

// COMPONENTS
import * as Icons from '@icons'
import Button from '@components/atoms/button'
import CharacterCount from '@components/atoms/character-count'
import CurrencyTextField from '@components/molecules/currency-textfield'
import GroupCell from '@components/molecules/group-cell'
import PledgeSlotFieldsMultiple from '@components/molecules/pledge-slot-fields-multiple'
import Spinner from '@components/atoms/spinner'
import StepTracker from '@components/organisims/step-tracker'
import TextField from '@components/atoms/textfield'
import { Column } from '@components/templates/column'

// ROUTES
import * as GroupActions from '../_localStore/actions'
import { PMRoute } from '../create/pm'
import { useGroupDispatch, useGroupSettings } from '../_localStore/_hooks'
import '../create/create.css'
import './recreate.css'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createResponseObj, VirtualCardAPI } from '@apis'
import { useCurrencyInput, useInput } from '@hooks/inputs'
import { flatten } from '@utils/string-helper'
import { groupMax, max, min } from '@constants'
import { isNonEmptyString } from '@utils/type-checkers'
import { parsePledge, parseTeraUserData } from '@schemas'
import { toPennies } from '@utils/number-helper'
import { queryClient } from '@/lib/queryClient'

const titleMax = 25
const sourceGroupPath = '/group/recreate'

const useSourceGroup = () => useOutletContext<Group>()

const Layout: FC = () => {
    const sourceGroup = useLoaderData() as Group
    const location = useLocation()
    const { isLoaded, isSignedIn, user } = useUser()
    const localDispatch = useGroupDispatch()
    const initializedGroupIdRef = useRef<string>()
    const [isReady, setIsReady] = useState(false)
    const currentStep = getCurrentStep(location.pathname, sourceGroupPath, ['/', '/pm'])
    const isOrganizer = isSignedIn ? sourceGroup.organizer?.id === user?.id : false
    const organizerPaymentMethod = useMemo(() => {
        const paymentMethod = getOrganizerPledge(sourceGroup)?.paymentMethod
        return paymentMethod?.type === 'credit_card' ? paymentMethod : undefined
    }, [sourceGroup])

    useEffect(() => {
        if (!isLoaded || !isOrganizer || sourceGroup.type === 'merchant') return
        if (initializedGroupIdRef.current === sourceGroup.id) return

        const organizerPledge = getOrganizerPledge(sourceGroup)
        localDispatch(GroupActions.setRequestBody(createRequestBody(sourceGroup)))
        localDispatch(GroupActions.setPledgeAmount((organizerPledge?.amount || 0) / 100))
        localDispatch(GroupActions.setPaymentMethod(organizerPaymentMethod))

        initializedGroupIdRef.current = sourceGroup.id
        setIsReady(true)
    }, [isLoaded, isOrganizer, localDispatch, organizerPaymentMethod, sourceGroup])

    useEffect(() => {
        return () => {
            initializedGroupIdRef.current = undefined
            localDispatch(GroupActions.resetGroupState())
        }
    }, [localDispatch])

    if (!isLoaded) return null
    if (!isOrganizer || sourceGroup.type === 'merchant') return <Navigate to={`/group/${sourceGroup.id}`} replace={true} />
    if (!isReady) return null

    return (
        <>
            <Column.Content className='group-banner-section' style={{ padding: '0rem' }}>
                <StepTracker currentStep={currentStep} steps={2} />
            </Column.Content>
            {currentStep === 2
                ? (
                    <Column.Content className='group-create-screen'>
                        <Outlet context={sourceGroup} />
                    </Column.Content>
                )
                : <Outlet context={sourceGroup} />
            }
        </>
    )
}

const RecreateGroupComponent: FC = () => {
    const sourceGroup = useSourceGroup()
    const navigate = useNavigate()
    const { isLoaded, user } = useUser()
    const { state, updateRequestBody } = useGroupSettings()
    const autoOpenedActiveGroupRef = useRef<string>()

    const linkPhysicalCard = !!state.requestBody.linkPhysicalCard
    const linkVirtualCard = !!state.requestBody.linkVirtualCard
    const storedPledges = state.requestBody.pledges || []

    const [title, titleProps] = useInput({ initValue: state.requestBody.title || sourceGroup.title || '' })
    const [targetAmount, targetAmountProps] = useCurrencyInput({
        initValue: `${(state.requestBody.targetAmount || 0) / 100}`,
        max: groupMax,
    })
    const [pledgeSlots, setPledgeSlots] = useState<Pledge[]>(storedPledges)
    const [isEditingPledge, setIsEditingPledge] = useState(false)
    const [activeGroup, setActiveGroup] = useState<Group>()
    const [gettingActiveGroup, setGettingActiveGroup] = useState(false)
    const [closingActiveGroup, setClosingActiveGroup] = useState(false)
    const [pendingCardSelection, setPendingCardSelection] = useState<'physical' | 'virtual'>()

    const physicalCard = user?.publicMetadata.tera?.physicalCard
    const virtualCard = user?.publicMetadata.tera?.virtualCard

    const showAddAnother = useMemo(() => {
        if (pledgeSlots.length < 1) return true
        return isPledgeSlotComplete(pledgeSlots[pledgeSlots.length - 1])
    }, [pledgeSlots])

    const participantsSubmittable = useMemo(() => pledgeSlots.every((pledge) => isPledgeSlotComplete(pledge)), [pledgeSlots])
    const titleSubmittable = !!title && title.length <= titleMax
    const goalSubmittable = !targetAmount || (toPennies(targetAmount) >= min && toPennies(targetAmount) <= groupMax)
    const activeGroupTransactions = useMemo(() => {
        if (!activeGroup?.transactions) return []

        return Object.keys(activeGroup.transactions)
            .map((key) => activeGroup.transactions?.[key] || [])
            .reduce((a, b) => a.concat(b), [])
    }, [activeGroup?.transactions])
    const activeGroupHasTransactions = activeGroupTransactions.length > 0
    const activeGroupRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const selectionKey = [
            sourceGroup.id,
            linkPhysicalCard ? physicalCard?.activeGroup || '' : '',
            linkVirtualCard ? virtualCard?.activeGroup || '' : '',
        ].join(':')

        if (autoOpenedActiveGroupRef.current === selectionKey) return

        if (linkPhysicalCard && isNonEmptyString(physicalCard?.activeGroup)) {
            autoOpenedActiveGroupRef.current = selectionKey
            setPendingCardSelection('physical')
            void getActiveGroup(physicalCard.activeGroup)
            return
        }

        if (linkVirtualCard && isNonEmptyString(virtualCard?.activeGroup)) {
            autoOpenedActiveGroupRef.current = selectionKey
            setPendingCardSelection('virtual')
            void getActiveGroup(virtualCard.activeGroup)
        }
    }, [linkPhysicalCard, linkVirtualCard, physicalCard?.activeGroup, sourceGroup.id, virtualCard?.activeGroup])

    function openActiveGroupWindow() {
        activeGroupRef.current?.showModal()
    }

    function closeActiveGroupWindow(resetSelection?: boolean) {
        if (resetSelection) {
            if (pendingCardSelection === 'physical') {
                updateRequestBody({ linkPhysicalCard: false })
            }

            if (pendingCardSelection === 'virtual') {
                updateRequestBody({ linkVirtualCard: false })
            }
        }

        activeGroupRef.current?.close()
        setClosingActiveGroup(false)
        setPendingCardSelection(undefined)
    }

    async function getActiveGroup(activeGroupId: string) {
        openActiveGroupWindow()
        setGettingActiveGroup(true)

        if (!isNonEmptyString(activeGroupId)) {
            throw createResponseObj({
                status: 'failed',
                type: 'local',
                name: 'missing-resources',
                message: `Missing required resources. ${JSON.stringify(activeGroupId)}`,
            })
        }

        const currentActiveGroup = await VirtualCardAPI.GetGroupRequest.fetch(activeGroupId)
        setActiveGroup(currentActiveGroup)
        setGettingActiveGroup(false)
    }

    async function closeActiveGroup() {
        setClosingActiveGroup(true)

        if (!isNonEmptyString(activeGroup?.id)) {
            throw createResponseObj({
                status: 'failed',
                type: 'local',
                name: 'missing-resources',
                message: `Missing required resources. ${JSON.stringify(activeGroup?.id)}`,
            })
        }

        if (!activeGroupHasTransactions) await VirtualCardAPI.CancelGroupRequest.fetch(activeGroup.id)
        else await VirtualCardAPI.CompleteGroupRequest.fetch(activeGroup.id)

        queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        if (user) {
            user.publicMetadata = {
                tera: parseTeraUserData({ ...user.publicMetadata.tera, activeGroup: undefined }),
            }
        }

        closeActiveGroupWindow(false)
    }

    function handleBack() {
        navigate(`/group/${sourceGroup.id}`)
    }

    function handleNext() {
        updateRequestBody({
            title,
            targetAmount: toPennies(targetAmount),
            pledges: pledgeSlots,
        })

        navigate('./pm')
    }

    function addPledgeSlot() {
        setPledgeSlots((current) => [...current, parsePledge({ name: undefined, amount: 0 })])
    }

    function removePledgeSlot(index: number) {
        setPledgeSlots((current) => {
            const arr = [...current]
            arr.splice(index, 1)
            return arr
        })
    }

    function updatePledgeSlot(index: number, name: string, amount: number) {
        setPledgeSlots((current) => {
            const arr = [...current]
            arr[index] = parsePledge({ name: name || undefined, amount })
            return arr
        })
    }

    function selectSingleUseCard() {
        updateRequestBody({
            linkPhysicalCard: false,
            linkVirtualCard: false,
        })
    }

    function togglePhysicalCard() {
        const nextChecked = !linkPhysicalCard
        updateRequestBody({
            linkPhysicalCard: nextChecked,
            linkVirtualCard: false,
        })

        if (nextChecked && isNonEmptyString(physicalCard?.activeGroup)) {
            setPendingCardSelection('physical')
            void getActiveGroup(physicalCard.activeGroup)
        }
    }

    function toggleVirtualCard() {
        const nextChecked = !linkVirtualCard
        updateRequestBody({
            linkPhysicalCard: false,
            linkVirtualCard: nextChecked,
        })

        if (nextChecked && isNonEmptyString(virtualCard?.activeGroup)) {
            setPendingCardSelection('virtual')
            void getActiveGroup(virtualCard.activeGroup)
        }
    }

    if (!isLoaded) return null

    return (
        <>
            <Column.Header.Start>
                <a onClick={handleBack}>
                    <img src='/icons/arrow-left.svg' alt='back' className='w-[1.4rem] h-[1.4rem]' draggable='false' />
                </a>
            </Column.Header.Start>
            <Column.Content className='group-recreate-screen'>
                <div className='group-recreate-section'>
                    <h2>Recreate Group</h2>
                    <p className='text-body-2 text-gray-500'>We copied the previous group details. Review and update anything before creating the new group.</p>
                </div>

                <div className='group-recreate-section'>
                    <h3>Payment Card</h3>
                    <div className='group-recreate-card-options'>
                        {!!virtualCard && (
                            <button
                                type='button'
                                className={flatten('group-recreate-card-option', linkVirtualCard ? 'selected' : '')}
                                onClick={toggleVirtualCard}
                            >
                                <div className='group-recreate-card-option-icon'>
                                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'><path d='M17 2l4 4-4 4' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /><path d='M3 11V9a4 4 0 014-4h14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /><path d='M7 22l-4-4 4-4' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /><path d='M21 13v2a4 4 0 01-4 4H3' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>
                                </div>
                                <strong>Reloadable</strong>
                                <span className='text-body-2 text-gray-500'>Last four: {virtualCard.lastFour}</span>
                            </button>
                        )}
                        <button
                            type='button'
                            className={flatten('group-recreate-card-option', (!linkPhysicalCard && !linkVirtualCard) ? 'selected' : '')}
                            onClick={selectSingleUseCard}
                        >
                            <div className='group-recreate-card-option-icon'>
                                <svg width='14' height='19' viewBox='0 0 14 19' fill='none'><path d='M10.8359 0.833252H2.5026C1.58213 0.833252 0.835938 1.57944 0.835938 2.49992V15.8333C0.835938 16.7537 1.58213 17.4999 2.5026 17.4999H10.8359C11.7564 17.4999 12.5026 16.7537 12.5026 15.8333V2.49992C12.5026 1.57944 11.7564 0.833252 10.8359 0.833252Z' stroke='currentColor' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round' /></svg>
                            </div>
                            <strong>Single Use</strong>
                            <span className='text-body-2 text-gray-500'>New virtual card</span>
                        </button>
                        {!!physicalCard && (
                            <button
                                type='button'
                                className={flatten('group-recreate-card-option', linkPhysicalCard ? 'selected' : '')}
                                onClick={togglePhysicalCard}
                            >
                                <div className='group-recreate-card-option-icon'>
                                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'><rect x='2' y='5' width='20' height='14' rx='2' stroke='currentColor' strokeWidth='2' /><path d='M2 10h20' stroke='currentColor' strokeWidth='2' /></svg>
                                </div>
                                <strong>Physical Card</strong>
                                <span className='text-body-2 text-gray-500'>Last four: {physicalCard.lastFour}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className='group-recreate-section'>
                    <h3>Group Details</h3>
                    <TextField
                        {...titleProps}
                        name='title'
                        placeholder='Cowboys Game, Concert Tickets, etc...'
                        inlineElementRight={<CharacterCount label={title} max={titleMax} />}
                        minLength={1}
                        maxLength={titleMax}
                        required
                    />
                    <CurrencyTextField centered={true} name='targetAmount' {...targetAmountProps} />
                    <p className='text-body-2 text-gray-500'>The goal amount is still optional & you can always adjust the goal later.</p>
                </div>

                <div className='group-recreate-section'>
                    <div className='group-recreate-section-heading'>
                        <div>
                            <h3>Participants</h3>
                        </div>
                    </div>
                    <div className='group-recreate-participants'>
                        {pledgeSlots.map((pledge, index) => (
                            <PledgeSlotFieldsMultiple
                                key={`${pledge.id || 'recreate-slot'}-${index}`}
                                addedPledge={false}
                                group={sourceGroup}
                                pledge={pledge}
                                pledgeSlots={pledgeSlots}
                                hidePledge={isEditingPledge && ((index + 1) === pledgeSlots.length)}
                                showRemove={pledgeSlots.length > 1 || !!pledge.name || pledge.amount > 0}
                                onPledgeEdit={setIsEditingPledge}
                                onPledgeChange={(name, amount) => updatePledgeSlot(index, name, amount)}
                                onPledgeRemove={() => removePledgeSlot(index)}
                            />
                        ))}
                        {!isEditingPledge && showAddAnother && (
                            <button type='button' className='group-recreate-add' onClick={addPledgeSlot}>
                                <Icons.Plus width='1.25rem' height='1.25rem' />
                                <span><strong>Add Another</strong></span>
                            </button>
                        )}
                    </div>
                </div>
            </Column.Content>
            <Column.Footer>
                <Button onClick={handleNext} disabled={!titleSubmittable || !goalSubmittable || !participantsSubmittable}>Next</Button>
            </Column.Footer>
            <dialog ref={activeGroupRef}>
                <div>
                    <div className='temp-card' style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                        <h2 style={{ textAlign: 'center' }}>This Group is Linked to Your Card</h2>
                        <p style={{ textAlign: 'center' }}>You can only have one group linked to your {activeGroup?.card?.cardType === 'physical' ? 'physical' : 'reloadable'} card at a time, you can finalize or tap keep activated to use a one time virtual card.</p>
                        {(gettingActiveGroup || !activeGroup)
                            ? (
                                <div className='flex flex-row items-center justify-center gap-2 py-12'>
                                    <Spinner indicator='circle' />
                                </div>
                            )
                            : (
                                <span style={{ pointerEvents: closingActiveGroup ? 'none' : undefined }} onClick={() => closeActiveGroupWindow(false)}>
                                    <GroupCell group={activeGroup}></GroupCell>
                                </span>
                            )}
                        <div className='flex flex-row gap-2.5'>
                            <Button className='flex-1' theme='light' disabled={closingActiveGroup} onClick={() => closeActiveGroupWindow(true)}>Keep Activated</Button>
                            <Button className='flex-1' theme='primary' disabled={closingActiveGroup} onClick={closeActiveGroup}>
                                {!activeGroupHasTransactions
                                    ? (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Cancel Group')
                                    : (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Close Group')}
                            </Button>
                        </div>
                    </div>
                </div>
            </dialog>
        </>
    )
}

const getCurrentStep = (pathname: string, root: string, paths: string[]) => {
    const currentPath = pathname.startsWith(root) ? pathname.slice(root.length) || '/' : '/'
    let currentStep = paths.length

    for (const path of [...paths].reverse()) {
        if (currentPath.includes(path)) break
        currentStep -= 1
    }

    return Math.max(currentStep, 1)
}

const getOrganizerPledge = (group: Group) => {
    return (group.pledges || []).find((pledge) => pledge.user?.id === group.organizer?.id || pledge.userId === group.organizer?.id)
}

const getPledgeName = (pledge: Pledge) => {
    if (isNonEmptyString(pledge.name)) return pledge.name
    const fullName = `${pledge.user?.givenName || ''} ${pledge.user?.familyName || ''}`.trim()
    return fullName
}

const createRequestBody = (group: Group): CreateGroupRequestBody => {
    const pledges = (group.pledges || [])
        .filter((pledge) => pledge.user?.id !== group.organizer?.id && pledge.userId !== group.organizer?.id)
        .map((pledge) => parsePledge({
            name: getPledgeName(pledge) || undefined,
            amount: pledge.amount,
        }))
        .filter((pledge) => isNonEmptyString(pledge.name) && pledge.amount > 0)

    return {
        title: group.title,
        targetAmount: group.targetAmount,
        linkPhysicalCard: !!group.linkPhysicalCard,
        linkVirtualCard: !!group.linkVirtualCard,
        pledges,
    }
}

const isPledgeSlotComplete = (pledge: Pledge) => !!pledge.name && pledge.amount >= min && pledge.amount <= max

// Group creation is disabled (FNT-744). Block the recreate flow entirely.
const loader = (args: LoaderFunctionArgs) => {
    const groupId = args.params['groupId']
    return redirect(isNonEmptyString(groupId) ? `/group/${groupId}` : '/account')
}

const IndexRoute: RouteObject = {
    index: true,
    Component: RecreateGroupComponent,
}

const RecreateGroupRoute: RouteObject = {
    path: 'recreate/:groupId',
    element: <Layout />,
    loader,
    shouldRevalidate: () => false,
    children: [
        IndexRoute,
        PMRoute,
    ],
}

export default RecreateGroupRoute
